/*
 I am not a component you should use directly. I just have to be broken out because hipsters.
 */
import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import classNames from 'classnames';
import { Classes } from '@blueprintjs/core';
import { debounce } from 'lodash';

import { formAriaProps, useFocusTrigger } from 'core/util/ariaUtils';
import Text from 'core/components/Text';
import SelectValuesRenderer from 'core/components/select/SelectValuesRenderer';

const MultiSelectInputWrapperJunk = styled.div`
  &.${Classes.INPUT} {
    cursor: pointer;
    display: inline-flex;
    flex-wrap: wrap;
    height: auto;
    min-height: 30px;
    max-height: 140px;
    align-items: center;
    gap: 4px;
    overflow: auto;
    padding: 4px;
  }

  &.${Classes.INPUT}.responsive {
    display: flex;
    flex-wrap: nowrap;
    max-height: none;
    overflow: hidden;
    width: 100%;
    min-width: 120px;
  }

  &.${Classes.DISABLED} {
    pointer-events: none;
  }
`;

const MultiSelectInput = React.forwardRef((props, ref) => {
  const {
    active,
    style,
    options,
    placeholder,
    onClick,
    onUnselectBuilder,
    tagFill,
    values,
    valueRenderer,
    valueTagProps,
    valueTagRenderer,
    className,
    disabled,
    responsive
  } = props;

  const [wrapperRef, triggerWrapperFocus] = useFocusTrigger(ref);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (onClick && !disabled) {
        onClick();
      }
    }
  };

  const delayedFocus = debounce(triggerWrapperFocus, 200);

  const handleUnselect = (value) => {
    if (onUnselectBuilder) {
      const removeFunc = onUnselectBuilder(value);
      return (e) => {
        removeFunc(e);
        delayedFocus();
      };
    }
    return null;
  };

  const ValueRenderer = valueTagRenderer || SelectValuesRenderer;
  const ariaProps = formAriaProps(props, { ariaHaspopup: 'listbox', ariaExpanded: active });
  ariaProps['aria-autocomplete'] = 'list';
  ariaProps['aria-haspopup'] = 'listbox';
  ariaProps.role = 'listbox';

  return (
    <MultiSelectInputWrapperJunk
      className={classNames({ [Classes.DISABLED]: disabled, responsive }, Classes.INPUT, className)}
      tabIndex="0"
      onClick={onClick}
      style={style}
      onKeyDown={handleKeyDown}
      {...ariaProps}
      ref={ref || wrapperRef}
    >
      {values.length > 0 && (
        <ValueRenderer
          onUnselectBuilder={handleUnselect}
          options={options}
          tagFill={tagFill}
          valueRenderer={valueRenderer}
          valueTagProps={valueTagProps}
          values={values}
          responsive={responsive}
        />
      )}
      {values.length === 0 && <Text muted>{placeholder}</Text>}
    </MultiSelectInputWrapperJunk>
  );
});

export default observer(MultiSelectInput);
